import { useCallback, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { UploadButton } from 'src/components/Buttons';
import {
  UploadBarcodesBody,
  UploadBarcodesForm,
} from 'src/components/UploadArtifacts/UploadBarcodes';
import { BarcodeForm } from 'src/components/UploadArtifacts/UploadBarcodes/UploadBarcodes.utils';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button } from 'src/core/ui';
import { useEventItemLoadingDisplay } from 'src/hooks/useEventItemLoadingDisplay';
import { CancellableFormFooter } from 'src/modals/common';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { ModalBody, ModalFooter, ModalProps } from 'src/modals/Modal';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  EntityWithRealTickets,
  withConfirmedTickets,
} from 'src/utils/ticketUtils';
import {
  PosClientConfig,
  PosUiActionResult,
  SaleMetrics,
  TicketBarcode,
} from 'src/WebApiController';

import { modalDetails } from '../common/Modals.css';
import { Summary } from '../common/Summary';
import {
  ModalBodyDataContainer,
  ModalBodyHeaderContainer,
} from '../Modal/Modal.styled';

export type UploadBarcodeProps = {
  onUploadBarcodes?: (
    posClientConfig: PosClientConfig,
    entityId: number,
    barcodes: TicketBarcode[]
  ) => Promise<PosUiActionResult>;
  cancelTo?: ModalProps;
  defaultToBulk?: boolean;
  isReadOnly?: boolean;
  loadingContentId: FormatContentId;
  searchingContentId: FormatContentId;
  errorContentId: FormatContentId;
};

export const UploadBarcodes = ({
  onUploadBarcodes,
  loadingContentId,
  searchingContentId,
  errorContentId,
  ...rest
}: UploadBarcodeProps) => {
  const { loadingState } = useEventItemLoadingDisplay<EntityWithRealTickets>(
    loadingContentId,
    searchingContentId,
    errorContentId
  );

  return (
    loadingState || (
      <UploadBarcodesContent onUploadBarcodes={onUploadBarcodes} {...rest} />
    )
  );
};

export type UploadBarcodeContentProps = Omit<
  UploadBarcodeProps,
  'loadingContentId' | 'searchingContentId' | 'errorContentId'
>;

export const UploadBarcodesContent = ({
  cancelTo,
  onUploadBarcodes,
  isReadOnly = false,
}: UploadBarcodeContentProps) => {
  const { closeModal, setModal } = useContext(ModalContext);
  const { posEntity: entityWithTickets, setActivePosEntity } =
    useActivePosEntityContext<EntityWithRealTickets>();
  const { refreshMetrics } = useCatalogMetricsContext<SaleMetrics>();

  const goBack = useCallback(
    async (isCancelled: boolean) => {
      if (!isCancelled) {
        refreshMetrics?.();

        // Refresh the active data so the SaleDetail dialog will have the new content
        await setActivePosEntity(
          entityWithTickets!.id,
          entityWithTickets!.idOnMkp,
          true
        );
      }

      if (cancelTo) {
        setModal(cancelTo);
      } else {
        closeModal(true);
      }
    },
    [
      cancelTo,
      refreshMetrics,
      setActivePosEntity,
      entityWithTickets,
      setModal,
      closeModal,
    ]
  );

  return (
    <UploadBarcodesForm
      entityWithTickets={withConfirmedTickets(entityWithTickets!)}
      onComplete={goBack}
      onUpload={onUploadBarcodes}
      renderContent={(onUploadClick: () => void, disabled?: boolean) => (
        <UploadBarcodesFormContent
          cancelTo={cancelTo}
          onUploadClick={onUploadClick}
          disabled={disabled || isReadOnly}
          goBack={goBack}
        />
      )}
    />
  );
};

const UploadBarcodesFormContent = ({
  cancelTo,
  onUploadClick,
  disabled,
  goBack,
}: {
  cancelTo?: ModalProps;
  onUploadClick: () => void;
  disabled?: boolean;
  goBack: (isCancelled: boolean) => void;
}) => {
  const { formState } = useFormContext<BarcodeForm>();
  const { isDirty, isSubmitting } = formState;

  const { event, posEntity } =
    useActivePosEntityContext<EntityWithRealTickets>();

  const entityWithTickets = useMemo(
    () => withConfirmedTickets(posEntity!),
    [posEntity]
  );

  return (
    <>
      <CancellableFormHeader
        disabled={isSubmitting}
        cancelTo={cancelTo}
        showDialogOnCancel={isDirty}
      >
        <ConnectedEventEntityHeader
          title={<Content id={ContentId.SaveBarcodes} />}
        />
      </CancellableFormHeader>
      <ModalBody>
        <ModalBodyHeaderContainer>
          <Summary event={event!} posEntity={posEntity!} />
        </ModalBodyHeaderContainer>
        <ModalBodyDataContainer>
          {isSubmitting || !posEntity ? (
            <PosSpinner />
          ) : (
            <div className={modalDetails}>
              <UploadBarcodesBody
                isReadOnly={disabled}
                ticketCnt={entityWithTickets.ticketCnt}
                seating={entityWithTickets.seating}
              />
            </div>
          )}
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        {/* We want to use isReadOnly here because isReadOnly is meant for this dialog is informational */}
        {!disabled && (
          <CancellableFormFooter
            data-testid="upload-barcodes-close-button"
            disabled={disabled || isSubmitting}
            cancelTo={cancelTo}
            showDialogOnCancel={isDirty}
          >
            <UploadButton
              disabled={disabled || isSubmitting}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onUploadClick();
              }}
              textContentId={ContentId.SaveBarcodes}
              data-testid="upload-barcodes-save-button"
            />
          </CancellableFormFooter>
        )}
        {disabled && (
          <Button onClick={() => goBack(true)} disabled={isSubmitting}>
            <Content id={ContentId.Back} />
          </Button>
        )}
      </ModalFooter>
    </>
  );
};
